import React from 'react';
import styled from 'styled-components';
import { File, FileText, FileSpreadsheet, Image as ImageIcon } from 'lucide-react';

interface FileTypeInfo {
  icon: React.ReactNode;
  color: string;
  bg: string;
  ext: string;
}

function getFileTypeInfo(name: string, type: string): FileTypeInfo {
  const ext = name.split('.').pop()?.toLowerCase() || '';

  if (type.startsWith('image/'))
    return { icon: <ImageIcon size={18} />, color: '#8B5CF6', bg: 'rgba(139,92,246,0.1)', ext: ext.toUpperCase() };

  switch (ext) {
    case 'pdf':
      return { icon: <FileText size={18} />, color: '#EF4444', bg: 'rgba(239,68,68,0.1)', ext: 'PDF' };
    case 'xls': case 'xlsx': case 'xlsm':
      return { icon: <FileSpreadsheet size={18} />, color: '#22C55E', bg: 'rgba(34,197,94,0.1)', ext: ext.toUpperCase() };
    case 'csv':
      return { icon: <FileSpreadsheet size={18} />, color: '#22C55E', bg: 'rgba(34,197,94,0.1)', ext: 'CSV' };
    case 'doc': case 'docx':
      return { icon: <FileText size={18} />, color: '#3B82F6', bg: 'rgba(59,130,246,0.1)', ext: ext.toUpperCase() };
    case 'ppt': case 'pptx':
      return { icon: <FileText size={18} />, color: '#F97316', bg: 'rgba(249,115,22,0.1)', ext: ext.toUpperCase() };
    case 'txt': case 'md':
      return { icon: <FileText size={18} />, color: '#6B7280', bg: 'rgba(107,114,128,0.1)', ext: ext.toUpperCase() };
    default:
      return { icon: <File size={18} />, color: '#6B7280', bg: 'rgba(107,114,128,0.1)', ext: ext.toUpperCase() || 'FILE' };
  }
}

/** Shared by uploaded attachments and the document chat header. */
export function FileTypeBadge({ name, type = '' }: { name: string; type?: string }) {
  const info = getFileTypeInfo(name, type);
  return (
    <FileIconBadge $bg={info.bg} $color={info.color} aria-hidden="true">
      {info.icon}
      <FileExtLabel $color={info.color}>{info.ext}</FileExtLabel>
    </FileIconBadge>
  );
}

const FileIconBadge = styled.div<{ $bg: string; $color: string }>`
  width: ${({ theme }) => theme.input.fileIconSize};
  height: ${({ theme }) => theme.input.fileIconSize};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  flex-shrink: 0;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ theme }) => theme.inputMobile.fileIconSize};
    height: ${({ theme }) => theme.inputMobile.fileIconSize};
  }

  svg { flex-shrink: 0; }
`;

const FileExtLabel = styled.span<{ $color: string }>`
  font-size: ${({ theme }) => theme.input.fileExtFontSize};
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${({ $color }) => $color};
  line-height: 1;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.inputMobile.fileExtFontSize};
  }
`;

